import React, { useMemo } from "react";
import { compact, get, map, reduce, take } from "lodash";
import { shorten } from "@backpacker/number";
import BubbleChart from "@charty/bubble-chart";
import BubbleChartTooltip from "@charty/tooltip/chartjs-bubble";
import { Color } from "@glossy";
import { getMetricLabel } from "@linkfluence/i18n";
import { Metric } from "@linkfluence/models/metric";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import {
  DatavizBubbleChart,
  DatavizScatterPlot,
  isScatterPlotDataviz,
} from "@radarly/models/dataviz";
import { FullFacet, Value } from "@radarly/models/facets";
import { SearchFragmentTerm } from "@radarly/models/search";
import Empty from "../../empty";
import { computeData } from "../../term/data";

export interface SharedWidgetsFieldMetricsBubbleChartInnerProps {
  data: SearchFragmentTerm["data"];
  fragment: SearchFragmentTerm;
  dataviz: DatavizBubbleChart | DatavizScatterPlot;
  facet: FullFacet;
  className?: string;
  onClickPoint?: (uid: string | number) => void;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
}

function getMetric(metrics: Metric[], index: number) {
  return metrics ? metrics[index] : null;
}

export default function SharedWidgetsFieldMetricsBubbleChartInner({
  data,
  fragment,
  facet,
  dataviz,
  className,
  onClickPoint,
  disableAnimation,
  isFullscreen,
}: SharedWidgetsFieldMetricsBubbleChartInnerProps) {
  const locale = useCurrentUserLocale();
  const metrics = isScatterPlotDataviz(dataviz)
    ? take(fragment.request.aggs[facet.id]?.metrics, 2)
    : fragment.request.aggs[facet.id]?.metrics;
  const primaryMetric = getMetric(metrics, 0);
  const secondaryMetric = getMetric(metrics, 1);
  const tertiaryMetric = getMetric(metrics, 2);

  const series = useMemo(() => {
    const values =
      data && data.aggs && facet && data.aggs[facet.id]
        ? data.aggs[facet.id].values
        : [];

    const counts = reduce(
      metrics,
      (acc, metric) => {
        acc[metric] = computeData(values, facet, metric);
        return acc;
      },
      {}
    );
    const generateSerie = (
      value: Value,
      x: number,
      y: number,
      size: number
    ) => ({
      id: "" + value.id,
      label: value.label,
      color: value.color || Color.Orange,
      data: [{ x, y, size }],
    });

    return compact(
      map(facet.values, (value) => {
        const x = get(counts, [primaryMetric, value.id], 0);
        const y = get(counts, [secondaryMetric, value.id], 0);
        const size = get(counts, [tertiaryMetric, value.id], 0);
        if (primaryMetric === Metric.NetSentiment && size > 0) {
          return generateSerie(value, x, y, size);
        }
        if (x > 0 && y > 0 && (!tertiaryMetric || size > 0)) {
          return generateSerie(value, x, y, size);
        }
        return null;
      })
    );
  }, [data, facet, metrics, primaryMetric, secondaryMetric, tertiaryMetric]);

  const getTooltipXValue = (xValue: number): string => {
    if (primaryMetric === Metric.NetSentiment) {
      return `${shorten(locale, xValue, 3, true, 1)}%`;
    }
    return shorten(locale, xValue);
  };

  return (
    <Empty empty={!series.length}>
      <BubbleChart
        series={series}
        XMetricLabel={getMetricLabel(locale, primaryMetric)}
        YMetricLabel={getMetricLabel(locale, secondaryMetric)}
        sizeMetricLabel={getMetricLabel(locale, tertiaryMetric)}
        XMetric={primaryMetric}
        chartTooltip={({ label, color, size, xValue, yValue }) => (
          <BubbleChartTooltip
            label={label}
            color={color}
            size={size}
            xValue={getTooltipXValue(xValue)}
            yValue={yValue}
            locale={locale}
            XMetricLabel={getMetricLabel(locale, primaryMetric)}
            YMetricLabel={getMetricLabel(locale, secondaryMetric)}
            sizeMetricLabel={getMetricLabel(locale, tertiaryMetric)}
          />
        )}
        locale={locale}
        className={className}
        onClickPoint={onClickPoint}
        scaleX={dataviz.scaleX || "linear"}
        scaleY={dataviz.scaleY || "linear"}
        disableAnimation={disableAnimation}
        isFullscreen={isFullscreen}
      />
    </Empty>
  );
}
